import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ChevronLeft, Phone, Mail, Bookmark, Home, BedDouble, Zap,
  ChevronUp, ChevronDown, AlertCircle, Check, Trash2, Pencil,
  TrendingDown, TrendingUp, Building2, ArrowDown, MessageSquarePlus, User,
  Moon, AlertTriangle, CheckCircle2, XCircle,
} from "lucide-react";
import http from "@/lib/http";
import { t } from "@/i18n";
import { useAuth } from "@/lib/auth";
import { formatEuro, daysSince, formatDateBE, parseDate } from "@/lib/format";
import PlatformLinks from "@/components/PlatformLinks";
import QuickCallDialog from "@/components/QuickCallDialog";
import KlapperPanel from "@/components/KlapperPanel";
import LeadTasks from "@/components/LeadTasks";
import useStatusLabelMap from "@/lib/useStatusLabelMap";
import useSyncHealth, { fmtDT } from "@/lib/useSyncHealth";

export default function LeadDetail() {
  const { user } = useAuth();
  const { id: leadId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const back = location.state?.from || "/leads";
  const navList = location.state?.list || null;
  const navIndex = navList ? navList.findIndex(l => l === leadId) : -1;

  const [lead, setLead] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState({});
  const [callOpen, setCallOpen] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // null | "archive" | "hard"
  const [archiveReason, setArchiveReason] = useState("");
  const statusMap = useStatusLabelMap();

  const archiveLead = async () => {
    await http.post(`/api/leads/${leadId}/archive`, { reason: archiveReason || null });
    setConfirmDelete(null);
    setArchiveReason("");
    navigate(back);
  };

  const hardDeleteLead = async () => {
    await http.delete(`/api/leads/${leadId}?hard=true`);
    setConfirmDelete(null);
    navigate(back);
  };

  const load = async () => {
    setLoadError(null);
    try {
      const r = await http.get(`/api/leads/${leadId}`);
      setLead(r.data);
      const [sr, ar, ur] = await Promise.all([
        http.get("/api/lead-statuses", { params: { source_type: r.data.source_type } }).catch(() => ({ data: [] })),
        http.get("/api/lead-actions").catch(() => ({ data: [] })),
        http.get("/api/users").catch(() => ({ data: [] })),
      ]);
      setStatusOptions(sr.data);
      setActions(ar.data.filter(a =>
        a.enabled && (a.applies_to || []).includes(r.data.source_type),
      ));
      const um = {}; (ur.data || []).forEach(u => { um[u.id] = u; }); setUsers(um);
    } catch (e) {
      setLoadError(e.response?.data?.detail
        || (e.response ? `Serverfout (${e.response.status})` : "Netwerkfout — controleer je verbinding."));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leadId]);

  // Keyboard nav: J/K + arrows + Esc back
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.key === "Escape") { navigate(back); }
      if (!navList) return;
      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        if (navIndex >= 0 && navIndex < navList.length - 1) {
          navigate(`/lead/${navList[navIndex + 1]}`, { state: location.state });
        }
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        if (navIndex > 0) {
          navigate(`/lead/${navList[navIndex - 1]}`, { state: location.state });
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navList, navIndex, navigate, back, location.state]);

  const setStatus = async (status) => {
    await http.patch(`/api/leads/${leadId}/status`, { status });
    await load();
  };
  const toggleShortlist = async () => {
    await http.patch(`/api/leads/${leadId}/shortlist`, { shortlisted: !lead.shortlisted });
    await load();
  };
  const executeAction = async (action) => {
    const done = lead.actions_done?.[action.key];
    if (done && !window.confirm(`"${action.label}" werd al uitgevoerd. Opnieuw uitvoeren?`)) return;
    await http.post(`/api/leads/${leadId}/actions/${action.key}`);
    await load();
  };

  if (!lead) {
    if (loadError) {
      return (
        <div className="flex-1 p-8" data-testid="lead-detail-error">
          <div className="max-w-md mx-auto mt-16 text-center">
            <AlertCircle size={28} className="mx-auto text-[var(--danger)] mb-3" />
            <div className="text-[15px] font-semibold text-[var(--ink-900)] mb-1">
              Dossier kon niet geladen worden
            </div>
            <div className="text-[13px] text-[var(--ink-500)] mb-4">{loadError}</div>
            <div className="flex items-center justify-center gap-2">
              <button type="button" onClick={() => load()} data-testid="lead-detail-retry"
                      className="btn btn-primary !h-9 !px-4 text-[13px]">
                Opnieuw proberen
              </button>
              <button type="button" onClick={() => navigate(back)} data-testid="lead-detail-error-back"
                      className="btn btn-ghost !h-9 !px-4 text-[13px]">
                Terug
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="flex-1 p-8 text-[var(--ink-500)] text-sm" data-testid="lead-detail-loading">
        {t("common.loading")}
      </div>
    );
  }

  const days = daysSince(lead.date_posted);
  const isReady = (days || 0) >= 90;
  const contactName = lead.source_type === "makelaar" ? lead.agent_name : lead.owner_name;
  const contactPhone = lead.source_type === "makelaar" ? lead.agent_phone : lead.owner_phone;
  const contactEmail = lead.source_type === "makelaar" ? lead.agent_email : lead.owner_email;
  const currentStatus = statusOptions.find(s => s.key === lead.status);
  const alerts = lead.status_alerts || [];

  return (
    <div className="flex-1 min-w-0 overflow-auto" data-testid="lead-detail-page">
      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-white/85 backdrop-blur border-b border-[var(--border-soft)]
                      px-8 py-3 flex items-center gap-3">
        <button type="button" onClick={() => navigate(back)} data-testid="lead-back"
                className="btn btn-ghost !h-9 !px-3 !text-[13px]">
          <ChevronLeft size={15} /> Terug
        </button>
        {navList && navList.length > 1 && (
          <div className="flex items-center gap-1 ml-2" data-testid="lead-nav">
            <button type="button" disabled={navIndex <= 0}
                    onClick={() => navigate(`/lead/${navList[navIndex - 1]}`, { state: location.state })}
                    data-testid="lead-nav-prev"
                    className="w-8 h-8 rounded-md grid place-items-center hover:bg-[var(--sand-100)] disabled:opacity-30">
              <ChevronUp size={15} />
            </button>
            <button type="button" disabled={navIndex >= navList.length - 1}
                    onClick={() => navigate(`/lead/${navList[navIndex + 1]}`, { state: location.state })}
                    data-testid="lead-nav-next"
                    className="w-8 h-8 rounded-md grid place-items-center hover:bg-[var(--sand-100)] disabled:opacity-30">
              <ChevronDown size={15} />
            </button>
            <span className="text-[12px] text-[var(--ink-500)] tabular ml-1">
              {t("leads.counter", { i: navIndex + 1, n: navList.length })}
            </span>
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className={`chip ${statusChipClass(currentStatus?.color)}`} data-testid="lead-status-chip">
            {currentStatus?.label || lead.status}
          </span>
          {(() => {
            // Online/offline chip — bron is Supabase (listing_urls).
            // Leeg → pand niet meer als zoekertje terug te vinden bij Mondo
            // Vastgoed; gevuld → pand staat nog online.
            const isOffline = !lead.listing_urls || lead.listing_urls.length === 0;
            return isOffline ? (
              <span className="chip flex items-center gap-1 bg-[#FBE7E7] text-[#7B2424] border-[#EFBABA]"
                    data-testid="lead-online-chip"
                    title="Pand staat niet meer als zoekertje online (geen URLs in Supabase)">
                <XCircle size={12} /> Offline
              </span>
            ) : (
              <span className="chip flex items-center gap-1 bg-[#E6F4EC] text-[#1F6F4A] border-[#A6D8B7]"
                    data-testid="lead-online-chip"
                    title="Pand staat nog online bij Mondo Vastgoed">
                <CheckCircle2 size={12} /> Online
              </span>
            );
          })()}
          {lead.shortlisted && (
            <span className="chip chip-teal flex items-center gap-1">
              <Bookmark size={12} /> Shortlist
            </span>
          )}
          {lead.in_option === true && (
            <span className="chip flex items-center gap-1 bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]"
                  data-testid="lead-in-option-chip"
                  title={lead.in_option_at ? `Sinds ${new Date(lead.in_option_at).toLocaleDateString("nl-BE")}` : ""}>
              <AlertCircle size={12} /> In optie
            </span>
          )}
          {lead.source_type === "makelaar" && lead.was_particulier && (
            <span className="chip flex items-center gap-1 bg-[var(--sand-100)] text-[var(--ink-700)] border-[var(--border-soft)]"
                  data-testid="lead-was-particulier-chip"
                  title="Dit pand stond ooit als particuliere verkoop online (zie geschiedenis hieronder)">
              ooit particulier
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className={`px-8 py-6 ${isReady ? "bg-[var(--teal-50)]" : ""}`}
           data-testid={isReady ? "lead-ready-bg" : undefined}>
        {/* Versheid van de gegevens — laatste geslaagde Mondo-sync + per-pand
            status-update. Verouderde data krijgt een aparte waarschuwingschip
            naast de laatst bekende status (die blijft gewoon zichtbaar). */}
        <FreshnessLine lead={lead} />
        {/* Offline-banner — verschijnt enkel wanneer Supabase / Mondo de
            zoekertje-URL's verwijderd heeft (= pand niet meer online bij de
            bron). We baseren ons NIET op de lokale "Check nu"-scraper, want
            die kan een tijdelijk fout-signaal geven door bot-blokkade of een
            netwerkprobleem. Supabase is de bron van waarheid. */}
        {(!lead.listing_urls || lead.listing_urls.length === 0) && (
          <div className="mb-5 flex items-start gap-3 p-4 rounded-lg
                          bg-[#FBE7E7] border border-[#EFBABA]"
               data-testid="lead-offline-banner">
            <AlertCircle size={18} className="text-[#B43232] mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-[#7B2424]">
                Pand offline — Mondo Vastgoed heeft de zoekertje-URL's verwijderd
              </div>
              <div className="text-[11px] text-[#9B3B3B] mt-0.5">
                Het pand staat niet meer als zoekertje online. Komt het opnieuw online met dezelfde ID,
                dan zal FlowNavigator automatisch de URL's terug aanvullen.
              </div>
            </div>
          </div>
        )}
        {/* Status alert banners — from configured remind_after_days rules */}
        {alerts.length > 0 && (
          <div className="space-y-2 mb-5" data-testid="lead-alerts">
            {alerts.map(a => (
              <div key={a.rule_id}
                   data-testid={`lead-alert-${a.rule_id}`}
                   className="flex items-start gap-3 p-4 rounded-lg
                              bg-[#FDF3DF] border border-[#EAD49B]">
                <AlertCircle size={18} className="text-[#8A5D10] mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="text-[13px] font-semibold text-[#5A3F0B]">
                    {a.message}
                  </div>
                  <div className="text-[11px] text-[#7A5618] mt-0.5">
                    Status "{a.status_label}" staat al {a.days_elapsed} dagen op deze lead
                    (drempel: {a.days_threshold} dagen).
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Hero — photo + title row */}
        <div className="grid grid-cols-12 gap-6 mb-6">
          <div className="col-span-12 md:col-span-4">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-[var(--sand-200)] grid place-items-center">
              {lead.photos?.[0] ? (
                <img src={lead.photos[0]} alt="" className="w-full h-full object-cover" />
              ) : (
                <Home size={48} className="text-[var(--ink-400)]" />
              )}
            </div>
          </div>
          <div className="col-span-12 md:col-span-8">
            <div className="text-[12px] uppercase tracking-wider text-[var(--ink-500)] mb-1">
              {lead.source_type === "makelaar" ? "Makelaar" : "Particulier"}
            </div>
            <EditableAddress lead={lead} onChanged={load} />
            {isReady && (
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full
                              bg-[var(--teal-700)] text-white text-[12px] font-semibold uppercase tracking-wider"
                   data-testid="lead-ready-badge">
                <AlertCircle size={13} /> ≥90d online — klaar voor prospectie
              </div>
            )}

            {/* Quick actions */}
            <div className="flex gap-2 mt-5 flex-wrap">
              <button type="button" data-testid="lead-action-call"
                      onClick={() => setCallOpen(true)}
                      className="btn btn-primary !h-10 !px-4">
                <Phone size={14} /> {t("leads.detail.call")}
              </button>
              {contactEmail && (
                <a href={`mailto:${contactEmail}`} data-testid="lead-action-mail"
                   className="btn btn-ghost !h-10 !px-4">
                  <Mail size={14} /> {t("leads.detail.mail")}
                </a>
              )}
              <button type="button" data-testid="lead-action-shortlist"
                      onClick={toggleShortlist}
                      className={`btn !h-10 !px-4 ${lead.shortlisted ? "btn-primary" : "btn-ghost"}`}>
                <Bookmark size={14} /> {t("leads.detail.toggle_shortlist")}
              </button>
              <button type="button" data-testid="lead-action-archive"
                      onClick={() => setConfirmDelete("archive")}
                      title="Pand op rust zetten — al je werk blijft bewaard, komt automatisch terug als het pand opnieuw online verschijnt"
                      className="btn !h-10 !px-4 btn-ghost">
                <Moon size={14} /> Op rust zetten
              </button>
              <button type="button" data-testid="lead-action-delete"
                      onClick={() => setConfirmDelete("hard")}
                      title="Definitief wissen — enkel voor testdata of duplicaten"
                      className="inline-flex items-center gap-1.5 h-10 px-3 text-[13px] font-semibold
                                 rounded-md border-2 border-[#B43232] bg-white text-[#B43232]
                                 hover:bg-[#FBE7E7] transition">
                <AlertTriangle size={14} /> Definitief wissen
              </button>
            </div>

            {/* Status chooser — placed right under quick actions for quick access */}
            <div className="mt-5" data-testid="status-chooser-block">
              <div className="text-[11px] uppercase tracking-wider text-[var(--ink-500)] mb-2 flex items-center gap-2">
                Status
                {currentStatus && (
                  <span className={`chip ${statusChipClass(currentStatus.color)} !text-[11px] !py-[2px]`}
                        data-testid="lead-current-status">
                    Huidig: {currentStatus.label}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2" data-testid="status-chooser">
                {statusOptions.map(s => {
                  const active = lead.status === s.key;
                  return (
                    <button key={s.id} type="button" data-testid={`lead-set-status-${s.key}`}
                            onClick={() => setStatus(s.key)}
                            className={`chip ${active ? statusChipClass(s.color, true) : ""}
                                         ${active ? "ring-2 ring-offset-1 ring-[var(--teal-500)] font-semibold" : ""}
                                         hover:border-[var(--teal-400)] transition`}>
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Actions row — Brief 1, Brief 2, … */}
            {actions.length > 0 && (
              <div className="mt-5" data-testid="lead-actions-row">
                <div className="text-[11px] uppercase tracking-wider text-[var(--ink-500)] mb-2">
                  Acties
                </div>
                <div className="flex flex-wrap gap-2">
                  {actions.map(a => {
                    const done = lead.actions_done?.[a.key];
                    return (
                      <ActionButton
                        key={a.id}
                        action={a}
                        done={done}
                        onExecute={() => executeAction(a)}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-7 space-y-5">
            <dl className="grid grid-cols-3 gap-4 text-[13px]">
              <Cell icon={Home} label={t("leads.detail.category")}
                    value={`${lead.property_category}${lead.property_subcategory ? ` · ${lead.property_subcategory}` : ""}`} />
              <Cell label={t("leads.detail.price")} value={formatEuro(lead.price)}
                    sub={lead.old_price ? `was ${formatEuro(lead.old_price)}` : null} />
              <Cell icon={BedDouble} label={t("leads.detail.bedrooms")} value={
                <BedroomsEditor lead={lead} onChanged={load} />
              } />
              <Cell icon={Zap} label={t("leads.detail.epc")} value={lead.epc ?? "—"} />
              {lead.source_type === "makelaar" && (
                <Cell label={t("leads.detail.source")}
                      value={lead.agent_name || "Makelaar"} />
              )}
              <Cell label={t("leads.detail.date_posted")} value={
                <DatePostedEditor lead={lead} onChanged={load} days={days} />
              } />
            </dl>

            {/* Eigenaar — handmatig bewerkbaar (gevuld vanuit Mondo voor particulier) */}
            <OwnerPanel lead={lead} onChanged={load} />

            {/* Notitie — beschikbaar op alle leads (auto-save in activiteiten) */}
            <LeadNotes lead={lead} onChanged={load} />

            <PlatformLinks lead={lead} onChanged={load} />
          </div>

          {/* Activity timeline (right column) */}
          <div className="col-span-12 md:col-span-5 space-y-5">
            <LeadTasks leadId={leadId} />
            <div className="card !p-4">
              <div className="text-[11px] uppercase tracking-wider text-[var(--ink-500)] mb-3">
                {t("leads.recent_activity")}
              </div>
              <ActivityTimeline lead={lead} statusMap={statusMap} />
            </div>
          </div>
        </div>

        {/* Klapper / Google Business */}
        <KlapperPanel lead={lead} onChanged={load} />

        {/* History from Mondo source */}
        <HistoryPanels lead={lead} />
      </div>

      {callOpen && (
        <QuickCallDialog lead={lead} onClose={() => setCallOpen(false)}
                         onSaved={() => { setCallOpen(false); load(); }} />
      )}

      {/* "Op rust" modal — soft delete, work preserved, auto-resurrect when relisted */}
      {confirmDelete === "archive" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
             data-testid="archive-modal"
             onClick={() => setConfirmDelete(null)}>
          <div onClick={(e) => e.stopPropagation()}
               className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-2 mb-1">
              <Moon size={18} className="text-[var(--teal-700)]" />
              <h3 className="font-display text-[20px] text-[var(--ink-900)]">Pand op rust zetten</h3>
            </div>
            <p className="text-[13px] text-[var(--ink-500)] mb-4">
              "{lead?.address || "deze lead"}" wordt op rust gezet. Al je werk
              (status, notities, eigenaar, shortlist) blijft bewaard. Komt het pand opnieuw
              online in Supabase, dan plaatst FlowNavigator de lead automatisch terug in de
              juiste pipeline.
            </p>
            <label className="block mb-4">
              <span className="text-[10px] uppercase tracking-wider text-[var(--ink-500)]">Reden (optioneel)</span>
              <select value={archiveReason}
                      onChange={(e) => setArchiveReason(e.target.value)}
                      data-testid="archive-reason-select"
                      className="mt-1 w-full h-10 px-3 rounded-md border border-[var(--border-strong)] text-[13px] focus:outline-none focus:border-[var(--teal-500)] bg-white">
                <option value="">— geen reden opgegeven —</option>
                <option value="Geen interesse">Geen interesse</option>
                <option value="Verkocht">Verkocht</option>
                <option value="Onbekend adres">Onbekend adres</option>
                <option value="Dubbel pand">Dubbel pand</option>
                <option value="Andere">Andere</option>
              </select>
            </label>
            <div className="flex justify-end gap-2">
              <button type="button"
                      onClick={() => { setConfirmDelete(null); setArchiveReason(""); }}
                      className="btn btn-ghost !h-9 !px-4 !text-[13px]">
                Annuleren
              </button>
              <button type="button"
                      onClick={archiveLead}
                      data-testid="archive-confirm"
                      className="btn btn-primary !h-9 !px-4 !text-[13px]">
                <Moon size={13} /> Op rust zetten
              </button>
            </div>
          </div>
        </div>
      )}

      {/* "Definitief wissen" — hard delete, irreversible, enkel voor testdata/duplicaten */}
      {confirmDelete === "hard" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
             data-testid="hard-delete-modal"
             onClick={() => setConfirmDelete(null)}>
          <div onClick={(e) => e.stopPropagation()}
               className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 border-t-4 border-[#B43232]">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={20} className="text-[#B43232]" />
              <h3 className="font-display text-[20px] text-[#B43232]">Definitief wissen</h3>
            </div>
            <p className="text-[13px] text-[var(--ink-700)] mb-2">
              Je staat op het punt om <strong>"{lead?.address || "deze lead"}"</strong> definitief te wissen.
            </p>
            <p className="text-[13px] text-[var(--ink-700)] mb-4 p-3 bg-[#FBE7E7] border border-[#EFBABA] rounded-md">
              Dit wist het pand en alle historie definitief. Gebruik dit enkel voor testdata of duplicaten,
              niet voor echte panden.
            </p>
            <div className="flex justify-end gap-2">
              <button type="button"
                      onClick={() => setConfirmDelete(null)}
                      data-testid="hard-delete-cancel"
                      className="btn btn-ghost !h-9 !px-4 !text-[13px]">
                Annuleren
              </button>
              <button type="button"
                      onClick={hardDeleteLead}
                      data-testid="hard-delete-confirm"
                      className="inline-flex items-center gap-1.5 h-9 px-4 text-[13px] font-semibold
                                 rounded-md border-2 border-[#B43232] bg-[#B43232] text-white
                                 hover:bg-[#9B2424]">
                <Trash2 size={13} /> Definitief wissen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ActivityTimeline({ lead, statusMap = {} }) {
  const acts = lead.activities || [];
  const [expanded, setExpanded] = React.useState(false);
  if (acts.length === 0) {
    return <div className="text-sm text-[var(--ink-500)] italic">{t("leads.no_activity_yet")}</div>;
  }
  const visible = expanded ? acts : acts.slice(0, 5);
  const hiddenCount = acts.length - visible.length;
  return (
    <>
      <ul className="space-y-3" data-testid="lead-activity-list">
        {visible.map(a => {
          const actor = a.actor_user_id ? lead.users?.[a.actor_user_id] : null;
          const initials = actor?.name
            ? actor.name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase()
            : "·";
          return (
            <li key={a.id} className="flex gap-3 text-[13px]">
              <div title={actor?.name || "Systeem"}
                   className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold tabular ${
                     actor
                       ? "bg-[var(--teal-700)] text-white"
                       : "bg-[var(--sand-100)] text-[var(--ink-400)]"
                   }`}>
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[var(--ink-900)]">
                  {actor && (
                    <span className="font-semibold mr-1">{actor.name}</span>
                  )}
                  {!actor && (
                    <span className="font-semibold mr-1 text-[var(--ink-500)]">Systeem</span>
                  )}
                  <span className="text-[var(--ink-700)]">{activityLabel(a, statusMap)}</span>
                </div>
                {a.payload?.note && <div className="text-[var(--ink-500)] mt-0.5 italic">"{a.payload.note}"</div>}
                <div className="text-[11px] text-[var(--ink-400)] mt-0.5">
                  {new Date(a.created_at).toLocaleString("nl-BE")}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {acts.length > 5 && (
        <button type="button"
                data-testid="activity-toggle"
                onClick={() => setExpanded(v => !v)}
                className="mt-3 text-[12px] text-[var(--teal-700)] hover:text-[var(--teal-800)] font-medium">
          {expanded
            ? "Toon minder"
            : `Toon alle ${acts.length} (+${hiddenCount} ouder)`}
        </button>
      )}
    </>
  );
}

function activityLabel(a, statusMap = {}) {
  const outcome = ({ no_answer: "geen gehoor", voicemail: "voicemail", answered: "opgenomen" })[a.payload?.outcome];
  const newKey = a.payload?.new_status;
  const newStatusLabel = newKey ? (statusMap[newKey] || newKey) : "";
  const map = {
    call: `Gesprek${outcome ? ` (${outcome})` : ""}`,
    status_change: `Status → ${newStatusLabel}`,
    shortlist: a.payload?.shortlisted ? "Op shortlist gezet" : "Van shortlist verwijderd",
    webhook_ingest: `Update via Flowzy`,
    note: "Notitie toegevoegd",
    assigned: "Toegewezen",
    released: "Vrijgegeven",
    platform_urls_update: "Platform-URLs aangepast",
    lead_action: `Actie: ${a.payload?.label || a.payload?.action_key}`,
    prospection_contact: `Prospectie-contact: ${a.payload?.name || a.payload?.phone || a.payload?.email || "(notitie)"}`,
    owner_update: (() => {
      const p = a.payload || {};
      const parts = [];
      if (p.name !== undefined) parts.push(p.name ? `naam=${p.name}` : "naam gewist");
      if (p.phone !== undefined) parts.push(p.phone ? `tel=${p.phone}` : "tel gewist");
      if (p.email !== undefined) parts.push(p.email ? `e-mail=${p.email}` : "e-mail gewist");
      return `Eigenaar bijgewerkt${parts.length ? ` — ${parts.join(", ")}` : ""}`;
    })(),
    archived: `Op rust gezet${a.payload?.reason ? ` — ${a.payload.reason}` : ""}`,
    restored: a.payload?.auto
      ? `Automatisch heractiveerd${a.payload?.reason ? ` — ${a.payload.reason}` : ""}`
      : "Manueel hersteld",
  };
  return map[a.type] || a.type.replace("_", " ");
}

function Cell({ icon: Icon, label, value, sub }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-[var(--ink-500)] flex items-center gap-1.5">
        {Icon && <Icon size={11} />} {label}
      </dt>
      <dd className="text-[14px] text-[var(--ink-900)] font-semibold tabular mt-0.5 capitalize">{value}</dd>
      {sub && <div className="text-[11px] text-[var(--ink-400)]">{sub}</div>}
    </div>
  );
}

function DatePostedEditor({ lead, onChanged, days }) {
  const [editing, setEditing] = useState(false);
  // <input type="date"> works with ISO YYYY-MM-DD; convert to/from our
  // DD/MM/YYYY storage format on the boundary.
  const toIso = (s) => {
    const d = parseDate(s);
    if (!d) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const fromIso = (iso) => {
    if (!iso) return null;
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    return `${m[3]}/${m[2]}/${m[1]}`;
  };
  const [draft, setDraft] = useState(toIso(lead.date_posted));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(toIso(lead.date_posted)); }, [lead.date_posted]);

  const persist = async (isoValue) => {
    if (saving) return;
    if ((isoValue || "") === toIso(lead.date_posted)) {
      // No change — just close
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await http.patch(`/api/leads/${lead.id}/date-posted`, {
        date_posted: fromIso(isoValue),
      });
      setEditing(false);
      await onChanged?.();
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1.5 normal-case">
        <input type="date" value={draft || ""}
               data-testid="date-posted-input"
               autoFocus
               disabled={saving}
               onChange={(e) => {
                 const v = e.target.value;
                 setDraft(v);
                 // Auto-save as soon as a complete date is picked
                 if (v && /^\d{4}-\d{2}-\d{2}$/.test(v)) persist(v);
               }}
               onBlur={() => persist(draft)}
               onKeyDown={(e) => {
                 if (e.key === "Escape") {
                   setEditing(false);
                   setDraft(toIso(lead.date_posted));
                 } else if (e.key === "Enter") {
                   persist(draft);
                 }
               }}
               className="h-8 px-2 rounded-md border border-[var(--border-strong)] text-[13px] tabular disabled:opacity-50" />
        {saving && <span className="text-[11px] text-[var(--ink-400)]">opslaan…</span>}
      </span>
    );
  }
  return (
    <button type="button" onClick={() => setEditing(true)}
            data-testid="date-posted-display"
            title={lead.date_posted ? `Online sinds ${formatDateBE(lead.date_posted)} — klik om te corrigeren`
                                    : "Klik om datum in te stellen"}
            className="text-left hover:text-[var(--teal-700)] transition-colors normal-case">
      {days !== null && days !== undefined ? `${days} dagen` : "—"}
      {lead.date_posted && (
        <span className="block text-[11px] text-[var(--ink-400)] font-normal mt-0.5">
          sinds {formatDateBE(lead.date_posted)}
        </span>
      )}
    </button>
  );
}

function statusChipClass(color, active = true) {
  if (!active) return "";
  switch (color) {
    case "teal":    return "chip-teal";
    case "warn":    return "chip-warn";
    case "danger":  return "chip-danger";
    case "success": return "chip-success";
    case "blue":    return "chip-blue";
    case "purple":  return "chip-purple";
    case "pink":    return "chip-pink";
    case "yellow":  return "chip-yellow";
    case "indigo":  return "chip-indigo";
    case "lime":    return "chip-lime";
    case "amber":   return "chip-amber";
    case "rose":    return "chip-rose";
    case "slate":   return "chip-slate";
    default:        return "";
  }
}

function actionButtonClass(color, done) {
  // Done = filled, vivid color. Not done = outline / ghost.
  if (done) {
    switch (color) {
      case "teal":    return "bg-[var(--teal-700)] text-white border-transparent hover:bg-[var(--teal-800)]";
      case "warn":    return "bg-[#D97706] text-white border-transparent hover:bg-[#B45309]";
      case "amber":   return "bg-[#B45309] text-white border-transparent hover:bg-[#92400E]";
      case "yellow":  return "bg-[#CA8A04] text-white border-transparent hover:bg-[#A16207]";
      case "lime":    return "bg-[#65A30D] text-white border-transparent hover:bg-[#4D7C0F]";
      case "success": return "bg-[#3B8C5C] text-white border-transparent hover:bg-[#2F7148]";
      case "blue":    return "bg-[#2563EB] text-white border-transparent hover:bg-[#1D4ED8]";
      case "indigo":  return "bg-[#4338CA] text-white border-transparent hover:bg-[#3730A3]";
      case "purple":  return "bg-[#7C3AED] text-white border-transparent hover:bg-[#6D28D9]";
      case "pink":    return "bg-[#DB2777] text-white border-transparent hover:bg-[#BE185D]";
      case "rose":    return "bg-[#E11D48] text-white border-transparent hover:bg-[#BE123C]";
      case "danger":  return "bg-[#D6646E] text-white border-transparent hover:bg-[#B84850]";
      case "slate":   return "bg-[#475569] text-white border-transparent hover:bg-[#334155]";
      default:        return "bg-[var(--ink-700)] text-white border-transparent hover:bg-[var(--ink-900)]";
    }
  }
  return "bg-white text-[var(--ink-900)] border-[var(--border-strong)] hover:border-[var(--teal-500)] hover:text-[var(--teal-700)]";
}

function ActionButton({ action, done, onExecute }) {
  const cls = actionButtonClass(action.color, !!done);
  const dt = done?.executed_at ? new Date(done.executed_at) : null;
  const dateStr = dt ? dt.toLocaleDateString("nl-BE") : null;
  return (
    <div className="flex flex-col items-start gap-0.5">
      <button
        type="button"
        onClick={onExecute}
        data-testid={`action-${action.key}`}
        className={`inline-flex items-center gap-2 h-10 px-4 rounded-lg border text-[13px] font-semibold transition-all ${cls}`}
      >
        {done && <Check size={14} />}
        {action.label}
      </button>
      {done && (
        <div className="text-[11px] text-[var(--ink-500)]" data-testid={`action-${action.key}-meta`}>
          {dateStr}
        </div>
      )}
    </div>
  );
}


function EditableAddress({ lead, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [addr, setAddr] = useState(lead.address || "");
  const [postal, setPostal] = useState(lead.postal_code || "");
  const [city, setCity] = useState(lead.city || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setAddr(lead.address || "");
    setPostal(lead.postal_code || "");
    setCity(lead.city || "");
  }, [lead.address, lead.postal_code, lead.city]);

  const isPlaceholder = !!lead.address_incomplete;
  const display = isPlaceholder ? "Adres onbekend" : (lead.address || "—");

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await http.patch(`/api/leads/${lead.id}/details`, {
        address: addr.trim() || null,
        postal_code: postal.trim() || null,
        city: city.trim() || null,
      });
      setEditing(false);
      await onChanged?.();
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="space-y-2 mb-2" data-testid="address-editor">
        <input
          autoFocus
          value={addr} onChange={(e) => setAddr(e.target.value)}
          placeholder="Straat + huisnummer"
          data-testid="address-input"
          className="w-full h-11 px-3 rounded-lg border-2 border-[var(--teal-500)] font-display text-[24px]"
        />
        <div className="flex gap-2">
          <input
            value={postal} onChange={(e) => setPostal(e.target.value)}
            placeholder="Postcode"
            data-testid="postal-input"
            className="w-28 h-9 px-2.5 rounded-md border border-[var(--border-strong)] text-[13px]"
          />
          <input
            value={city} onChange={(e) => setCity(e.target.value)}
            placeholder="Stad"
            data-testid="city-input"
            className="flex-1 h-9 px-2.5 rounded-md border border-[var(--border-strong)] text-[13px]"
          />
          <button type="button" onClick={save} disabled={saving}
                  data-testid="address-save"
                  className="btn btn-primary !h-9 !px-3 !text-[12px]">
            <Check size={13} /> {saving ? "Opslaan…" : "Opslaan"}
          </button>
          <button type="button"
                  onClick={() => { setEditing(false); setAddr(lead.address || ""); setPostal(lead.postal_code || ""); setCity(lead.city || ""); }}
                  className="btn btn-ghost !h-9 !px-3 !text-[12px]">
            Annuleren
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group" data-testid="address-display">
      <h1 className={`font-display text-[34px] leading-tight inline-flex items-center gap-2
                      ${isPlaceholder ? "text-[var(--ink-500)] italic" : "text-[var(--ink-900)]"}`}
          data-testid="lead-title">
        {display}
        <button type="button" onClick={() => setEditing(true)}
                data-testid="address-edit"
                title="Adres aanpassen"
                className={`p-1.5 rounded-md ${isPlaceholder ? "" : "opacity-0 group-hover:opacity-100"} transition-opacity
                            text-[var(--ink-400)] hover:bg-[var(--sand-100)] hover:text-[var(--teal-700)]`}>
          <Pencil size={16} />
        </button>
      </h1>
      <div className="text-[15px] text-[var(--ink-500)] mt-1">
        {lead.postal_code} {lead.city}
        {isPlaceholder && (
          <span className="ml-2 chip chip-warn !text-[10px] !py-[1px]" data-testid="address-incomplete-chip">
            Onvolledig — vul aan
          </span>
        )}
      </div>
    </div>
  );
}


function BedroomsEditor({ lead, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(lead.bedrooms ?? "");
  useEffect(() => { setVal(lead.bedrooms ?? ""); }, [lead.bedrooms]);

  const save = async () => {
    const n = val === "" ? null : parseInt(val, 10);
    await http.patch(`/api/leads/${lead.id}/details`, { bedrooms: Number.isFinite(n) ? n : null });
    setEditing(false);
    await onChanged?.();
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1.5 normal-case">
        <input type="number" min="0" value={val} autoFocus
               data-testid="bedrooms-input"
               onChange={(e) => setVal(e.target.value)}
               onBlur={save}
               onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
               className="w-16 h-8 px-2 rounded-md border border-[var(--teal-500)] text-[14px] tabular" />
      </span>
    );
  }
  return (
    <button type="button" onClick={() => setEditing(true)}
            data-testid="bedrooms-display"
            title="Slaapkamers aanpassen"
            className="text-left hover:text-[var(--teal-700)] transition-colors normal-case">
      {lead.bedrooms ?? <span className="italic text-[var(--ink-400)]">— klik om in te vullen</span>}
    </button>
  );
}


function OwnerPanel({ lead, onChanged }) {
  // Always show — owner contact info is the heart of the prospection flow
  // (filled from Supabase for particulier; empty/manual for makelaar).
  const [name, setName] = useState(lead.owner_name || "");
  const [phone, setPhone] = useState(lead.owner_phone || "");
  const [email, setEmail] = useState(lead.owner_email || "");
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const savingRef = React.useRef(false);
  const dirtyRef = React.useRef(false);
  const fieldRefs = {
    name: React.useRef(null), phone: React.useRef(null), email: React.useRef(null),
  };

  // Re-sync local state if the lead is reloaded from server
  useEffect(() => {
    if (savingRef.current || dirtyRef.current) return;
    setName(lead.owner_name || "");
    setPhone(lead.owner_phone || "");
    setEmail(lead.owner_email || "");
  }, [lead.owner_name, lead.owner_phone, lead.owner_email]);

  const fieldHasFocus = () => Object.values(fieldRefs).some(r => r.current === document.activeElement);

  const persist = React.useCallback(async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      await http.patch(`/api/leads/${lead.id}/owner`, {
        name: name.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
      });
      dirtyRef.current = false;
      setSavedAt(Date.now());
      await onChanged?.();
    } catch (e) {
      console.error("owner save failed", e);  // eslint-disable-line no-console
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [lead.id, name, phone, email, onChanged]);

  // Debounced auto-save: 1.2s after last keystroke and only if focus left the panel
  useEffect(() => {
    if (!dirtyRef.current) return;
    const id = setTimeout(() => {
      if (fieldHasFocus()) return;
      persist();
    }, 1200);
    return () => clearTimeout(id);
  }, [name, phone, email, persist]);  // eslint-disable-line react-hooks/exhaustive-deps

  const onChange = (setter) => (e) => { dirtyRef.current = true; setter(e.target.value); };
  const onBlurAny = () => {
    setTimeout(() => {
      if (savingRef.current || !dirtyRef.current) return;
      if (fieldHasFocus()) return;
      persist();
    }, 200);
  };

  const isParticulier = lead.source_type === "particulier";
  const fromSource = isParticulier && (lead.owner_name || lead.owner_phone || lead.owner_email);
  // Makelaar-lead die ooit particulier was: de owner-velden zijn behouden
  // ondanks de overgang. Maak duidelijk dat dit "oude" particulier-info is.
  const hasLegacyOwnerInfo =
    lead.source_type === "makelaar"
    && (lead.owner_name || lead.owner_phone || lead.owner_email);

  return (
    <div className="card !p-5 border-l-4 !border-l-[var(--teal-500)]" data-testid="owner-panel">
      <div className="flex items-center gap-2 mb-3">
        <User size={16} className="text-[var(--teal-700)]" />
        <h3 className="font-display text-[16px] text-[var(--ink-900)]">Eigenaar</h3>
        {fromSource && (
          <span className="chip !text-[10px] !py-[1px] bg-[var(--teal-50)] text-[var(--teal-700)] border-[var(--teal-200)]">
            uit Mondo
          </span>
        )}
        <span className="ml-auto text-[11px] text-[var(--ink-400)] tabular" data-testid="owner-save-indicator">
          {saving ? "Opslaan…" : (savedAt ? "Opgeslagen ✓" : "")}
        </span>
      </div>

      {hasLegacyOwnerInfo && (
        <div className="mb-4 px-3 py-2 rounded-md bg-[#FCEBC9]/60 border border-[#E5C9A0]
                        flex items-start gap-2"
             data-testid="legacy-owner-banner">
          <AlertCircle size={14} className="text-[#7C5A1E] shrink-0 mt-0.5" />
          <div className="text-[12px] text-[#5C4314] leading-relaxed">
            <span className="font-semibold">Vorige particulier-info bewaard.</span>
            {" "}Dit pand stond ooit als <em>particuliere</em> verkoop online en
            staat nu bij een makelaar. Onderstaande contactgegevens komen uit
            die periode — controleer of ze nog kloppen vóór je belt.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[var(--ink-500)]">Naam</span>
          <input
            ref={fieldRefs.name}
            value={name}
            onChange={onChange(setName)}
            onBlur={onBlurAny}
            placeholder="Voornaam achternaam"
            data-testid="owner-name"
            className="mt-1 w-full h-10 px-3 rounded-md border border-[var(--border-strong)] text-[14px] focus:outline-none focus:border-[var(--teal-500)]"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[var(--ink-500)]">Telefoonnummer</span>
          <div className="relative mt-1">
            <Phone size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-400)]" />
            <input
              ref={fieldRefs.phone}
              type="tel"
              value={phone}
              onChange={onChange(setPhone)}
              onBlur={onBlurAny}
              placeholder="0471 23 45 67"
              data-testid="owner-phone"
              className="w-full h-10 pl-9 pr-3 rounded-md border border-[var(--border-strong)] text-[14px] tabular focus:outline-none focus:border-[var(--teal-500)]"
            />
          </div>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[var(--ink-500)]">E-mail</span>
          <div className="relative mt-1">
            <Mail size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-400)]" />
            <input
              ref={fieldRefs.email}
              type="email"
              value={email}
              onChange={onChange(setEmail)}
              onBlur={onBlurAny}
              placeholder="naam@example.be"
              data-testid="owner-email"
              className="w-full h-10 pl-9 pr-3 rounded-md border border-[var(--border-strong)] text-[14px] focus:outline-none focus:border-[var(--teal-500)]"
            />
          </div>
        </label>
      </div>

      {/* Quick actions when contact info is filled in */}
      {(phone || email) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {phone && (
            <a href={`tel:${phone}`} className="btn btn-ghost !h-8 !px-3 !text-[12px]"
               data-testid="owner-phone-tel"
               onClick={(e) => e.stopPropagation()}>
              <Phone size={12} /> Bellen
            </a>
          )}
          {email && (
            <a href={`mailto:${email}`} className="btn btn-ghost !h-8 !px-3 !text-[12px]"
               data-testid="owner-email-mailto"
               onClick={(e) => e.stopPropagation()}>
              <Mail size={12} /> Mailen
            </a>
          )}
        </div>
      )}
    </div>
  );
}


function LeadNotes({ lead, onChanged }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const savingRef = React.useRef(false);
  const valueRef = React.useRef("");
  valueRef.current = text;
  const taRef = React.useRef(null);

  const save = React.useCallback(async () => {
    if (savingRef.current) return;
    const v = valueRef.current.trim();
    if (!v) return;
    savingRef.current = true;
    setSaving(true);
    try {
      await http.post(`/api/leads/${lead.id}/note`, { note: v });
      setText("");
      setSavedAt(Date.now());
      await onChanged?.();
    } catch (e) {  // noqa
      // swallowed — we surface error via console only since UI is single-field
      console.error("note save failed", e);  // eslint-disable-line no-console
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [lead.id, onChanged]);

  // Auto-save 2s after the last keystroke when focus has moved out of
  // the textarea, so we don't interrupt while typing.
  useEffect(() => {
    if (!text.trim()) return;
    const id = setTimeout(() => {
      if (document.activeElement === taRef.current) return;
      save();
    }, 2000);
    return () => clearTimeout(id);
  }, [text, save]);

  const handleBlur = () => {
    setTimeout(() => {
      if (savingRef.current) return;
      if (document.activeElement === taRef.current) return;
      if (valueRef.current.trim()) save();
    }, 300);
  };

  return (
    <div className="card !p-4" data-testid="lead-notes-card">
      <div className="text-[11px] uppercase tracking-wider text-[var(--ink-500)] mb-3 flex items-center justify-between">
        <span className="flex items-center gap-1.5">
          <MessageSquarePlus size={12} /> Notitie
        </span>
        <span className="normal-case font-normal text-[var(--ink-400)]" data-testid="note-save-indicator">
          {saving ? "Opslaan…" : (savedAt ? "Opgeslagen in activiteiten ✓" : "")}
        </span>
      </div>
      <textarea
        ref={taRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={handleBlur}
        placeholder="Schrijf hier je observaties — wordt automatisch opgeslagen in de activiteiten-tijdlijn rechts."
        rows={3}
        data-testid="lead-note-input"
        className="w-full px-3 py-2 rounded-md border border-[var(--border-strong)] text-[13px]
                   focus:outline-none focus:border-[var(--teal-500)] resize-y leading-relaxed"
      />
    </div>
  );
}


function HistoryPanels({ lead }) {
  const priceHist = lead.price_history || [];
  const agentHist = lead.agent_history || [];
  if (!priceHist.length && !agentHist.length) return null;

  const fmtPrice = (n) =>
    n == null ? "—" : new Intl.NumberFormat("nl-BE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

  // Total trend across the entire price history (from first listing to current)
  const firstPrice = priceHist.length > 0 ? priceHist[priceHist.length - 1] : null;
  const lastPrice = priceHist.length > 0 ? priceHist[0] : null;
  const totalDelta =
    firstPrice && lastPrice && typeof firstPrice.amount === "number" && typeof lastPrice.amount === "number"
      ? { diff: lastPrice.amount - firstPrice.amount, pct: firstPrice.amount ? ((lastPrice.amount - firstPrice.amount) / firstPrice.amount) * 100 : 0 }
      : null;

  return (
    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="history-panels">
      {/* Prijsgeschiedenis */}
      {priceHist.length > 0 && (
        <div className="card !p-5" data-testid="price-history-card">
          <div className="flex items-center gap-2 mb-1">
            <TrendingDown size={15} className="text-[var(--teal-700)]" />
            <h3 className="font-display text-[16px] text-[var(--ink-900)]">Prijsgeschiedenis</h3>
            <span className="text-[11px] text-[var(--ink-400)] ml-auto tabular">
              {priceHist.length} {priceHist.length === 1 ? "stap" : "stappen"}
            </span>
          </div>
          <p className="text-[12px] text-[var(--ink-500)] mb-4">
            Chronologisch — meest recente prijs bovenaan.
            {totalDelta && totalDelta.diff !== 0 && (
              <>
                {" "}Sinds eerste vermelding{" "}
                <span className={`font-semibold ${totalDelta.diff < 0 ? "text-[#3B8C5C]" : "text-[#B43232]"}`}>
                  {totalDelta.diff > 0 ? "+" : ""}{fmtPrice(totalDelta.diff)} ({totalDelta.pct > 0 ? "+" : ""}{totalDelta.pct.toFixed(1)}%)
                </span>.
              </>
            )}
          </p>

          <ol className="relative pl-5 border-l-2 border-[var(--border-soft)] space-y-3">
            {priceHist.map((p, i) => {
              const older = priceHist[i + 1];
              let delta = null;
              if (older && typeof older.amount === "number" && typeof p.amount === "number" && older.amount !== p.amount) {
                const diff = p.amount - older.amount;
                delta = { diff, pct: older.amount ? (diff / older.amount) * 100 : 0 };
              }
              const isLatest = i === 0;
              const isOriginal = i === priceHist.length - 1 && priceHist.length > 1;
              return (
                <li key={`${p.date || ""}-${p.amount}-${i}`} data-testid={`price-history-row-${i}`}
                    className="relative">
                  <span className={`absolute -left-[27px] top-1 w-3.5 h-3.5 rounded-full border-2 border-white
                                    ${isLatest ? "bg-[var(--teal-700)] ring-2 ring-[var(--teal-200)]" : "bg-[var(--ink-300)]"}`} />
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className={`tabular ${isLatest ? "text-[18px] font-display text-[var(--ink-900)]" : "text-[14px] text-[var(--ink-500)]"}`}>
                      {fmtPrice(p.amount)}
                    </span>
                    <span className="text-[11px] text-[var(--ink-500)] tabular">{p.date || "—"}</span>
                    {isLatest && (
                      <span className="chip chip-teal !text-[10px] !py-[1px]">Huidige prijs</span>
                    )}
                    {isOriginal && (
                      <span className="chip !text-[10px] !py-[1px] bg-[var(--sand-100)] text-[var(--ink-500)] border-[var(--border-soft)]">
                        Eerste vermelding
                      </span>
                    )}
                  </div>
                  {delta && (
                    <div className={`text-[11px] mt-0.5 inline-flex items-center gap-1 font-medium ${
                      delta.diff < 0 ? "text-[#3B8C5C]" : "text-[#B43232]"
                    }`}>
                      {delta.diff < 0 ? <ArrowDown size={11} /> : <TrendingUp size={11} />}
                      {delta.diff > 0 ? "+" : ""}{fmtPrice(delta.diff)}
                      <span className="text-[var(--ink-400)] font-normal">
                        ({delta.pct > 0 ? "+" : ""}{delta.pct.toFixed(1)}% t.o.v. vorige)
                      </span>
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {/* Makelaargeschiedenis */}
      {agentHist.length > 0 && (
        <div className="card !p-5" data-testid="agent-history-card">
          <div className="flex items-center gap-2 mb-1">
            <Building2 size={15} className="text-[var(--teal-700)]" />
            <h3 className="font-display text-[16px] text-[var(--ink-900)]">Makelaargeschiedenis</h3>
            <span className="text-[11px] text-[var(--ink-400)] ml-auto tabular">
              {agentHist.length} {agentHist.length === 1 ? "makelaar" : "makelaars"}
            </span>
          </div>
          <p className="text-[12px] text-[var(--ink-500)] mb-4">
            Opeenvolgende makelaars — meest recente bovenaan.
          </p>

          <ol className="relative pl-5 border-l-2 border-[var(--border-soft)] space-y-3">
            {agentHist.map((a, i) => {
              const isLatest = i === 0;
              const isOriginal = i === agentHist.length - 1 && agentHist.length > 1;
              return (
                <li key={`${a.date || ""}-${a.name || ""}-${i}`} data-testid={`agent-history-row-${i}`} className="relative">
                  <span className={`absolute -left-[27px] top-1 w-3.5 h-3.5 rounded-full border-2 border-white
                                    ${isLatest ? "bg-[var(--teal-700)] ring-2 ring-[var(--teal-200)]" : "bg-[var(--ink-300)]"}`} />
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className={`${isLatest ? "text-[15px] font-semibold text-[var(--ink-900)]" : "text-[13px] text-[var(--ink-700)]"}`}>
                      {a.name || "—"}
                    </span>
                    <span className="text-[11px] text-[var(--ink-500)] tabular">{a.date || "—"}</span>
                    {isLatest && (
                      <span className="chip chip-teal !text-[10px] !py-[1px]">Huidige makelaar</span>
                    )}
                    {isOriginal && (
                      <span className="chip !text-[10px] !py-[1px] bg-[var(--sand-100)] text-[var(--ink-500)] border-[var(--border-soft)]">
                        Eerste makelaar
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}

function FreshnessLine({ lead }) {
  const { health } = useSyncHealth();
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 text-[11px] text-[var(--ink-500)]"
         data-testid="lead-freshness">
      <span>
        Gegevens gecontroleerd via Mondo-sync:{" "}
        <strong className="text-[var(--ink-700)]">
          {health?.last_success_at ? fmtDT(health.last_success_at) : "nog geen geslaagde sync"}
        </strong>
        {lead.live_status_at && (
          <> · status van dit pand laatst bijgewerkt: {fmtDT(lead.live_status_at)}</>
        )}
      </span>
      {health?.stale && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-[#FDF3DF] text-[#8A5D10] border-[#EAD49B] font-medium"
              data-testid="lead-freshness-stale"
              title="De synchronisatie met Supabase loopt achter — deze status kan verouderd zijn.">
          <AlertTriangle size={10} /> mogelijk verouderd
        </span>
      )}
      {health?.last_status === "error" && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-[#FBE7E7] text-[#7B2424] border-[#EFBABA] font-medium"
              data-testid="lead-freshness-error">
          <AlertTriangle size={10} /> sync-fout
        </span>
      )}
    </div>
  );
}
